/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useRef, useState } from 'react'
import ReactPlayer from 'react-player'

import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Map } from 'components/Map'
import { Scroller } from 'components/Scroller'
import { Image } from 'components/Image'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import mapboxgl from 'mapbox-gl'
import {
  spotLayerStyle,
  circleLayerStyle,
  symbolLayerStyle,
  lineLayerStyle,
  shapeLayerStyle,
  setLayerSource,
  industryLayerStyle,
  cityLabelsStyle,
  circleFill,
  cirlceOpacityMobile,
  cirlceOpacity,
  symbolAbnnotationStyle,
  viewSwitzerland,
  toggleVisibility,
  spotsRed,
  spotsFullOpacity,
  spotsLowOpacity
} from './layerStyles'
import { MapRelatedBox, TextBox, TitleBox } from 'components/Boxes'
import textbg from './assets/textbg.png'
import textbg_white from './assets/textbg_white.png'
import textbg_main from './assets/textbg_main.png'
import markerarrow from './assets/markerarrow.png'
const isMobile = window.innerWidth < 711
const isLarge = window.innerWidth >= 1750
const isSmall = window.innerWidth < 340

const spotdata = require('./geojson/all_firms.json')
spotdata.features = spotdata.features.map(m => {
  m.properties.n_firmen = +m.properties.n_firmen
  return m
})
const industrydata = require('./geojson/industry_firms.json')
industrydata.features = industrydata.features.map(m => {
  m.properties.n_firmen = +m.properties.n_firmen
  return m
})

const geojsonsources = [
  { id: 'baarerstrasse', json: require('./geojson/baarerstrasse.json') },
  { id: 'lombard', json: require('./geojson/lombard.json') },
  { id: 'hotspots', json: require('./geojson/hotspots.json') },
  { id: 'metalli', json: require('./geojson/metalli.json') },
  { id: 'eurochem', json: require('./geojson/eurochem.json') },
  { id: 'nordstream', json: require('./geojson/nordstream.json') },
  { id: 'snb', json: require('./geojson/snb.json') },
  { id: 'bratschi', json: require('./geojson/bratschi.json') },
  { id: 'paykin', json: require('./geojson/paykin.json') },
  { id: 'usmanov', json: require('./geojson/usmanov.json') },
  { id: 'citylabels', json: require('./geojson/citylabels.json') },
  { id: 'spots', json: spotdata },
  { id: 'industry', json: industrydata }
]

// TODO Set the correct article ID here
const ARTICLE_ID = '394066292602'

export function AppComponent() {
  const currentScrollID = useRef('none')
  const currentScrollPOS = useRef(0)
  const [showScroll, setShwoScroll] = useState(true)
  const map = useRef<mapboxgl.Map>()

  const changeScrollId = (id: string) => {
    if (currentScrollID.current !== id) {
      currentScrollID.current = id
      currentScrollPOS.current = window.scrollY
    }
  }
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Wie sich die Russen in der Schweizer Geschäftswelt etablierten
            </h1>
            <h2>
              Seit 2005 hat sich die Zahl der Unternehmen mit russischer
              Beteiligung mehr als versechsfacht. Eine Auswertung von 5,5
              Millionen Einträgen der Schweizer Handelsregister gibt erstmals
              Einblick in teilweise verschwiegene Geschäfte.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical  very fitted segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Martin Stoll, Dominik Balmer, Patrick Meier, Sebastian Broschinski
            </div>
            <div className='ta updated'>Aktualisiert am 29. März 2022</div>
          </div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            Die Russinnen und Russen sind in der Schweizer Geschäftswelt überaus
            aktiv. Das Ausmass ihres Wirkens lässt sich jetzt erstmals anhand
            von Daten der Schweizer Handelsregister beziffern.
          </div>
        </div>
      </div>

      <div className='ui vertical very fitted segment'>
        <Scroller
          endSpace={isMobile ? 80 : 100}
          distance={160}
          front={[
            {
              o: {
                id: 'PREV',
                distance: 100,
                changeStyle: [spotsRed]
              },
              e: <div></div>
            },

            {
              o: {
                id: 'START',
                distance: 50,
                ...viewSwitzerland,
                changeStyle: [spotsRed]
              },
              e: (
                <>
                  <TitleBox>Die Zahlen</TitleBox>
                  <MapRelatedBox>
                    Von insgesamt 5,5 Millionen Einträgen seit 2005 hat das
                    Tamedia-Datenteam all{' '}
                    <span className='red'>
                      jene von russischen Staatsbürgern
                    </span>{' '}
                    analysiert.
                    <div className='bgswitch'>
                      <div className='note'>
                        Die Punkte auf der Karte zeigen jeweils eine im
                        Handelsregister eingetragene Adresse, an der mindestens
                        eine Firma auf eine Person mit russischem Pass
                        registriert ist.
                      </div>
                    </div>
                  </MapRelatedBox>
                </>
              )
            },

            {
              o: {
                id: 'TEXT_1',
                distance: 100,
                ...viewSwitzerland,

                changeStyle: [spotsRed, spotsFullOpacity],
                visibility: [
                  { id: 'spots', visible: true },
                  { id: 'industry', visible: false }
                ]
              },
              e: (
                <TextBox>
                  <p>
                    Das Resultat: 2005 war bei 330 Schweizer Unternehmen
                    mindestens ein russischer Staatsbürger im Handelsregister
                    eingetragen. In den Jahren danach stieg die Zahl stark an.
                    Im Januar 2022 waren es über 2100 Unternehmen. Das ist
                    sechsmal mehr.
                  </p>
                  <AutofitIFrame
                    title='Anzahl Firmen mit Russen im Handelsregister'
                    aria-label='Säulen (gestapelt)'
                    id='datawrapper-chart-H86wR'
                    src='https://datawrapper.dwcdn.net/H86wR'
                    scrolling='no'
                    style={{ border: ' none' }}
                    width='600'
                    height='400'
                  />
                  <p>
                    Die Daten stammen von der Wirtschaftsauskunft Crif AG. Im
                    Handelsregister sind die rechtlichen Verhältnisse von
                    Unternehmen vermerkt, beispielsweise die Verwaltungsräte von
                    Aktiengesellschaften, die Inhaber von Einzelfirmen, die
                    Geschäftsführer oder die Zeichnungsberechtigten.
                  </p>
                  <p>
                    Im gleichen Zeitraum ist auch die Zahl der übrigen Ausländer
                    in den Schweizer Handelsregistern stark gestiegen.
                    Fokussiert man allerdings nur auf die wichtigen Branchen
                    Grosshandel, IT, Finanzen und Versicherungen sowie
                    Management-Dienstleistungen, ist die Zunahme der russischen
                    Staatsbürger überproportional.
                  </p>
                  <p>
                    Die hohe Zahl der Schweizer Firmen unter russischer
                    Beteiligung ist das Resultat der guten Beziehung zwischen
                    beiden Ländern, die mit dem Einzug Wladimir Putins 1999 in
                    den Grossen Kremlpalast ihren Anfang nahm. Drei Jahre später
                    lobte Maxim Artamonov, Generalsekretär der in Genf
                    niedergelassenen Swiss-Russian Business Association, die
                    ausgezeichneten Beziehungen zwischen den russischen und
                    schweizerischen Behörden, «besonders zu den amtlichen
                    Wirtschaftsförderungsinstituten». Das «kontinuierliche und
                    dynamische Wachstum», das Artamonov damals rühmte, war
                    indessen erst der Anfang.
                  </p>
                  <Image
                    file={'putin.jpg'}
                    source={'kremlin.ru'}
                    caption={
                      'Die goldene Amtskette als Symbol der Macht: Kremlherrscher Wladimir Putin im Jahr 1999 mit Boris Jelzin.'
                    }
                  />
                  <p>
                    Mark Pieth, ehemaliger Strafrechtsprofessor der Universität
                    Basel und Geldwäscherei-Experte, vermutet, dass die Zunahme
                    der russisch beeinflussten Unternehmen in der Schweiz vor
                    allem mit dem Wachstum des Rohstoffhandels zusammenhängt.
                    Dieser wird zu grossen Teilen über die Schweiz abgewickelt.
                    Russland ist weltweit einer der grössten
                    Rohstoff-Exporteure, insbesondere von Öl und Gas. Gemäss dem
                    Aussendepartement EDA stiegen die Nettoeinnahmen aus dem
                    Rohstoffhandelsplatz Schweiz von 2 Milliarden Franken im
                    Jahr 2002 auf rund 25 Milliarden Franken im Jahr 2017.
                  </p>
                  <Image
                    file={'rohstoff_1.jpg'}
                    source={'Yuri Kochetko/EPA'}
                    caption={
                      'Eine Pumpstation zur Ölförderung im russischen Priobskoe. Der Staat hat fast überall seine Finger im Spiel.'
                    }
                  />
                </TextBox>
              )
            },

            {
              o: {
                id: 'BRANCHEN_MAP',
                distance: 100,

                center: isMobile
                  ? [7.556667489321861, 46.81691974729707]
                  : [8.226667, 46.801111],
                zoom: isSmall ? 5.5 : isMobile ? 5.8 : isLarge ? 7.8 : 7.2,
                annotations: [{ type: 'text', id: 'hotspots' }]
              },
              e: (
                <>
                  <TitleBox>Die Hotspots</TitleBox>
                  <MapRelatedBox>
                    Die russischen Firmen sind vor allem aus den klassischen
                    Finanzzentren heraus aktiv: aus{' '}
                    <span className='red'>
                      Zug, Zürich, Genf sowie entlang des Lac Léman und aus dem
                      Kanton Tessin.
                    </span>
                  </MapRelatedBox>
                </>
              )
            },

            {
              o: {
                id: 'Text_3',
                distance: 100,
                center: isMobile
                  ? [7.556667489321861, 46.81691974729707]
                  : [8.226667, 46.801111],
                zoom: isSmall ? 5.5 : isMobile ? 5.8 : isLarge ? 7.8 : 7.2,

                visibility: [{ id: 'city_labels', visible: false }],
                annotations: [{ type: 'text', id: 'hotspots' }]
              },
              e: (
                <TextBox>
                  <p>
                    Besonders beliebt bei Russen ist der Kanton Zug. Hier sind
                    laut der Auswertung knapp 400 aktive Unternehmen
                    domiziliert, die mindestens einen russischen Staatsbürger im
                    Handelsregister eingetragen haben. 285 von ihnen sind in den
                    Branchen Grosshandel, IT, Finanzen und Versicherungen sowie
                    Management-Dienstleistungen tätig. Mit diesen Zahlen
                    übertrifft Zug die Zentren Genf und Zürich. Wie gross der
                    Einfluss russischer Staatsbürger und des russischen Staates
                    auf diese Unternehmen ist, lässt sich anhand der Zahlen
                    nicht abschliessend sagen. Mit Sicherheit sind auch
                    «westlich» orientierte Firmen darunter.
                  </p>
                  <p>
                    <AutofitIFrame
                      title='Anzahl Firmen mit Russen im Handelsregister'
                      aria-label='Säulen (gestapelt)'
                      id='datawrapper-chart-H86wR'
                      src='https://datawrapper.dwcdn.net/UVZUM/'
                      scrolling='no'
                      style={{ border: ' none' }}
                      width='600'
                      height='500'
                    />
                  </p>
                  <p>
                    Für Martin Hilti, Geschäftsführer von Transparency
                    International Schweiz, ist das Resultat der
                    Handelsregister-Auswertung ein Beleg für «die Nachlässigkeit
                    der Schweiz in der Geldwäscherei- und
                    Korruptionsbekämpfung». Weder das Handelsregister noch sonst
                    ein behördlich geführtes Register enthalte Informationen
                    darüber, wer am betreffenden Unternehmen wirtschaftlich
                    berechtigt sei, sagt Hilti. Doch gerade diese Informationen
                    seien «eminent wichtig bei der Bekämpfung der Geldwäscherei
                    und Korruption».
                  </p>
                  <p>
                    Wie wenig die Verantwortlichen tatsächlich wissen, zeigt das
                    Beispiel des Kantons Zug. Anfang März sprachen die Behörden
                    von 40 Firmen mit einem «russischen Konnex» oder mit
                    «direktem Bezug zu Russland». Bernhard Neidhart, Leiter des
                    Amts für Wirtschaft und Arbeit des Kantons Zug, sagt heute
                    dazu: «Es war uns bewusst, dass diese 40 Firmen aufgrund der
                    fehlenden statistischen Möglichkeiten nur eine Teilmenge
                    darstellen.»
                  </p>
                </TextBox>
              )
            },
            {
              o: {
                id: 'ZUG',
                distance: 100,
                center: [8.516946936050658, 47.17451907620819],
                zoom: 12,
                visibility: [{ id: 'city_labels', visible: true }],
                changeStyle: [spotsRed]
              },
              e: (
                <>
                  <TitleBox>Das Beispiel Zug</TitleBox>
                  <TextBox>
                    Klar ist: Nirgends in der Schweiz ist die Dichte an russisch
                    geprägten Unternehmen so gross wie in der Innenstadt Zugs.
                    Hier sind russische Unternehmen im gewinnträchtigen
                    Grosshandel aktiv, vorab mit Gas, Eisen und chemischen
                    Produkten.
                  </TextBox>
                </>
              )
            },

            {
              o: {
                id: 'Baarerstrasse_18',
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13 : 14.25,
                annotations: [{ type: 'line', id: 'baarerstrasse' }],
                changeStyle: [
                  {
                    id: 'spots',
                    prop: 'circle-color',
                    value: circleFill
                  },

                  {
                    id: 'spots',
                    prop: 'circle-opacity',
                    value: isMobile ? cirlceOpacityMobile : cirlceOpacity
                  }
                ]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    Entlang der{' '}
                    <span className='yellow'>Baarerstrasse in Zug</span> reiht
                    sich ein Russengeschäft ans andere.
                  </p>
                  <p>
                    An vielen Adressen ist im Handelsregister mehr als ein
                    Unternehmen mit russischen Akteuren eingetragen.
                  </p>
                  <div className=''>
                    <div className='colorkey'>
                      <div className='keytitle'>Anzahl Unternehmen</div>
                      <div className='entry'>
                        <div
                          className='swatch'
                          style={{ backgroundColor: '#BA7FB7' }}
                        />
                        <div className='label'>1</div>
                      </div>{' '}
                      <div className='entry'>
                        <div
                          className='swatch'
                          style={{ backgroundColor: '#A757A3' }}
                        />

                        <div className='label'>2 - 3</div>
                      </div>{' '}
                      <div className='entry'>
                        <div
                          className='swatch'
                          style={{ backgroundColor: '#7A3F77' }}
                        />
                        <div className='label'>4 - 7</div>
                      </div>{' '}
                      <div className='entry'>
                        <div
                          className='swatch'
                          style={{ backgroundColor: '#202346' }}
                        />
                        <div className='label'>8+</div>
                      </div>
                    </div>
                  </div>
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'Baarerstrasse_18',
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13 : 14.25,
                annotations: [
                  { type: 'line', id: 'baarerstrasse' },
                  { type: 'symbol', id: 'metalli' }
                ],
                changeStyle: [
                  {
                    id: 'spots',
                    prop: 'circle-color',
                    value: circleFill
                  },

                  {
                    id: 'spots',
                    prop: 'circle-opacity',
                    value: isMobile ? cirlceOpacityMobile : cirlceOpacity
                  }
                ]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    Über dem{' '}
                    <span className='red'>Einkaufszentrum «Metalli»</span>{' '}
                    wickelt die Firma Metal Trade Overseas SA seit Jahren ihre
                    Geschäfte ab. Das Unternehmen gehört zum Konzern Norilsk
                    Nickel des russischen Metall-Oligarchen Wladimir Potanin.
                    Nur wenige Schritte entfernt hat eine Firma des Oligarchen
                    Oleg Deripaska seinen Zuger Sitz.
                  </p>
                  <Image
                    file={'potanin.jpg'}
                    source={'Ivan Sekretarev / AFP via Getty Images'}
                    caption={
                      'Oligarch Wladimir Potanin mit Kremlchef Putin an den Olympischen Winterspielen 2014 in Sotschi.'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'Baarerstrasse_37',
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13.5 : 14.25,
                annotations: [
                  { type: 'line', id: 'baarerstrasse' },
                  { type: 'symbol', id: 'eurochem' }
                ]
              },
              e: (
                <MapRelatedBox>
                  An der «Strasse der Russen» niedergelassen ist auch der{' '}
                  <span className='red'>Düngemittelhersteller Eurochem</span>{' '}
                  mit Hauptaktionär Andrei Melnitschenko. Der Rohstoffmagnat
                  gehört zu den reichsten Menschen der Schweiz. Er hat seinen
                  Wohnsitz in St. Moritz und ist mit Sanktionen belegt.
                  Begründung: «Er gehört zum einflussreichsten Kreis russischer
                  Geschäftsleute mit engen Verbindungen zur russischen
                  Regierung.»
                  <Image
                    file={'melnitschenko.jpg'}
                    source={'Andrey Rudakov / Bloomberg via Getty Images'}
                    caption={
                      'Einer der reichsten Menschen der Schweiz: Der Oligarch Andrei Melnitschenko.'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'Baarerstrasse_52',
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13.5 : 14.25,
                annotations: [
                  { type: 'line', id: 'baarerstrasse' },
                  { type: 'symbol', id: 'nordstream' }
                ]
              },
              e: (
                <MapRelatedBox>
                  Ein paare Türen weiter firmiert{' '}
                  <span className='red'>Nord Stream 2 AG.</span> Deren
                  Angestellten sind inzwischen entlassen, die Büros leer. Die
                  Pipeline Nord Stream 2 hätte Gas direkt aus Russland nach
                  Deutschland pumpen sollen. Die deutsche Regierung stoppte
                  wegen des Ukraine-Kriegs die Zertifizierung.
                  Verwaltungsratspräsident der Firma, die vom ehemaligen
                  DDR-Agenten Matthias Warnig geführt wird, ist der ehemalige
                  deutsche Kanzler Gerhard Schröder.
                  <Image
                    file={'gerhardundvladimir.jpg'}
                    source={'Reuters'}
                    caption={
                      'Dicke Freunde? Der frühere deutsche Bundeskanzler Gerhard Schröder mit Putin an dessen 53. Geburtstag in St. Petersburg.'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'video',
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13.5 : 14.25,
                annotations: [{ type: 'line', id: 'baarerstrasse' }]
              },
              e: (
                <TextBox>
                  <div className='video'>
                    <ReactPlayer
                      playing={false}
                      playsinline
                      width='100%'
                      height='100%'
                      controls
                      url={[
                        'https://interaktiv.tagesanzeiger.ch/2022/russen-im-schweizer-handelsregister/220325_Nawalny_Putin_ap_opt.mp4',
                        'https://interaktiv.tagesanzeiger.ch/2022/russen-im-schweizer-handelsregister/220325_Nawalny_Putin_ap_opt.webm'
                      ]}
                    />
                  </div>
                  <p>
                    In seinem Enthüllungsvideo spricht der Kremlkritiker Alexei
                    Nawalny mehrmals die Verflechtungen in die Schweiz an. In
                    diesem Zusammenhang erwähnt er den früheren DDR-Agenten
                    Matthias Warnig, den Geschäftsführer der Nord Stream 2 AG in
                    Zug. Das Nawalny-Video ist mit mehr als 100 Millionen Klicks
                    ein Internet-Hit.{' '}
                  </p>
                </TextBox>
              )
            },

            {
              o: {
                id: 'Chapter_3',
                distance: 100,
                center: isMobile
                  ? [8.516946936050658, 47.17451907620819]
                  : [8.517976333016604, 47.177388675046586],
                zoom: isMobile ? 13.5 : 14.25
              },
              e: (
                <>
                  <TitleBox>Die Helfer</TitleBox>
                  <TextBox>
                    <p>
                      Jahrelang haben Schweizer Anwälte und Treuhänder von ihrer
                      russischen Klientel gut gelebt. Das diskrete Geschäft war
                      umfassend: Aufbau von Firmenstrukturen, Organisieren einer
                      Aufenthaltsbewilligung, der Kauf einer Immobilie,
                      Beschaffen von Bankkonten und Organisieren der
                      Privatschule für die Kinder.
                    </p>
                    <p>
                      Mit den Sanktionen geraten jetzt auch solche Dienstleister
                      in den Fokus von Ermittlern. Denn Länder wie die USA legen
                      Sanktionsverfahren breit aus. Möglich, dass die nächste
                      Ferienreise eines Schweizer Finanzdienstleisters nach
                      Italien in Auslieferungshaft endet.
                    </p>
                  </TextBox>
                </>
              )
            },

            {
              o: {
                id: 'Text_4',
                distance: 100,
                center: isMobile
                  ? [8.541251988408058, 47.36560644034082]
                  : [8.539442111236804, 47.37172860951878],
                zoom: isMobile ? 13 : 14.25,
                annotations: [{ type: 'circle', id: 'snb' }]
              },
              e: (
                <MapRelatedBox>
                  Der laut Datenauswertung fleissigste sogenannte Enabler agiert
                  diskret und im Schatten vom Zürcher Finanzdistrikt aus. Unweit
                  der <span className='red'>Schweizerischen Nationalbank</span>{' '}
                  hat er sein Büro. Sein Name ist in der Öffentlichkeit
                  unbekannt. Bei insgesamt 48 Firmen mit russischer Klientel
                  trat der Mann über die Jahre als Verwalter und Vermittler auf:
                  Vom Edelstein-Handel über Medizin-Investment-Gesellschaften
                  bis hin zu Logistikunternehmen. Telefonisch ist er in den
                  letzten Tagen schwer erreichbar. Mit Journalisten spricht er
                  nicht. Der Slogan des 66-Jährigen:
                  <div className='quote'>
                    «Das grosse Geld
                    <br />
                    mag Ruhe.»
                  </div>
                </MapRelatedBox>
              )
            },

            {
              o: {
                center: isMobile
                  ? [8.541251988408058, 47.36560644034082]
                  : [8.539442111236804, 47.37172860951878],
                zoom: isMobile ? 13 : 14.25,
                visibility: [{ id: 'city_labels', visible: true }]
              },
              e: (
                <TextBox>
                  <p>
                    Seinen Kunden präsentiert er sich im Internet vor einem
                    hellgrünen Hintergrund, zufrieden lächelnd, mit aufgeföhnter
                    Frisur und knallgelber Krawatte. So verspricht er «absolute
                    Vertraulichkeit» und solide und kreative Geschäftslösungen.
                  </p>
                  <Image
                    file={'b_.jpg'}
                    source={'Screenshot'}
                    caption={
                      'Keiner macht fleissiger Geschäfte mit den Russen als dieser Strohmann, der sein Büro aktuell in Zürich hat. Sein Slogan: «Das grosse Geld mag Ruhe.» '
                    }
                  />
                  <p>
                    Eine seiner Dienstleistungen dürfte bei der russischen
                    Kundschaft gegenwärtig besonders geschätzt sein: Die
                    Rückführung von im Ausland investiertem Kapital. Die
                    «Repatriierung» von Geld mithilfe von Holdingstrukturen und
                    Krediten von Schweizer Banken sei eine «logische Lösung»,
                    schreibt der Treuhänder.
                  </p>
                  <p>
                    Für diejenigen, die bleiben wollen, bietet der umtriebige
                    Geschäftsmann «medizinische Leistungen auf 5-Stern-Niveau»,
                    Schönheitsoperationen und den Termin in einer Geburtsklinik
                    an – oder auch ein Scheidungsverfahren.
                  </p>
                </TextBox>
              )
            },
            {
              o: {
                id: 'Chapter_4',
                distance: 100,
                center: isMobile
                  ? [8.541251988408058, 47.36560644034082]
                  : [8.539442111236804, 47.37172860951878],
                zoom: isMobile ? 13 : 14.25
              },
              e: (
                <>
                  <TitleBox>Die Sanktionierten</TitleBox>
                  <TextBox>
                    <p>
                      Mit dem Ukraine-Krieg und den gegen Russland erhobenen
                      Sanktionen wurde die langjährige schweizerisch-russische
                      Freundschaft aufgekündigt. Viele gerne gesehene,
                      schwerreiche Russen wurden über Nacht zu Personae non
                      gratae.
                    </p>
                  </TextBox>
                </>
              )
            },
            {
              o: {
                id: 'Bratshci',
                center: isMobile
                  ? [8.541251988408058, 47.36560644034082]
                  : [8.539442111236804, 47.37172860951878],
                zoom: isMobile ? 13 : 14.25,
                annotations: [{ type: 'symbol', id: 'bratschi' }]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    So war die Aufforderung, abgeschickt von der Zürcher{' '}
                    <span className='red'>Anwaltskanzlei Bratschi,</span> klar
                    und unmissverständlich: Er möge die «New Economic School
                    Foundation» Stiftung bitte verlassen. Pjotr Olegowitsch
                    Awen, Gründer der mächtigen russischen Alfa Group und ein
                    angeblich enger Vertrauter Putins, kam der Bitte
                    stillschweigend nach.
                  </p>
                  <Image
                    file={'aven.jpg'}
                    source={'Alexander Nomenov / POOL / AFP'}
                    caption={
                      'Oligarch Pjotr Awen erhält im Jahr 2005 einen Orden und Applaus von Kremlchef Putin.'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'Text_5',
                ...viewSwitzerland,

                visibility: [{ id: 'city_labels', visible: false }]
              },
              e: (
                <TextBox>
                  <p>
                    Als Stiftungsrat war der schwerreiche Oligarch in der «New
                    Economic School Foundation» untragbar geworden, nachdem ihn
                    auch die Schweiz auf die Sanktionsliste gesetzt hatte.
                  </p>
                  <Image
                    file={'hess.jpg'}
                    source={'Gaetan Bally / Keystone'}
                    caption={
                      'Arbeitet mit Oligarch Pjotr Awen zusammen: Der frühere Obwaldner FDP-Ständerat Hans Hess.'
                    }
                  />
                  <p>
                    Domiziliert ist die Stiftung beim ehemaligen FDP-Ständerat
                    Hans Hess in Sarnen OW. Vor einigen Tagen liess sich Awen
                    aus dem Schweizer Stiftungsregister streichen.
                  </p>

                  <p>
                    Dabei war Awen, der in Saanenmöser bei Gstaad eine
                    (inzwischen behördlich gesperrte) Ferienwohnung besitzt, ein
                    von der helvetischen Wirtschaftselite gern gesehener Gast.
                    Namhafte Schweizer Firmen haben der Stiftung Geld zur
                    Verfügung gestellt, damit Schweizer Studierende an die
                    private Moskauer Wirtschaftsschule geschickt werden konnten.
                  </p>
                  <Image
                    file={'gstaad.jpg'}
                    source={'pd'}
                    caption={
                      'In diesem Gstaader Hotel besitzt Oligarch Pjotr Awen eine mittlerweile behördlich gesperrte Ferienwohnung. '
                    }
                  />
                </TextBox>
              )
            },
            {
              o: {
                id: 'Lombard',
                distance: 100,
                center: isMobile
                  ? [6.146195694628796, 46.193580525253395]
                  : [6.14693166748441, 46.19910831062394],
                zoom: isMobile ? 13 : 14.25,
                annotations: [{ type: 'symbol', id: 'lombard' }]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    Zu den Sponsoren gehörte auch die{' '}
                    <span className='red'>Genfer Privatbank Lombard Odier</span>
                    . «Wir freuen uns, junge und talentierte Menschen zu
                    unterstützen, die in einigen Jahren die Weltwirtschaft
                    bestimmen werden», begründete die Bank ihr Engagement 2011.
                  </p>
                  <p>
                    Für die Stiftung werde es in der Zukunft schwierig werden,
                    sagt Stiftungssekretär Florian S. Jörg – «wie für so vieles,
                    das zwischen der Schweiz und Russland gewachsen ist».
                  </p>
                </MapRelatedBox>
              )
            },
            {
              o: {
                id: 'Paykin',
                center: isMobile
                  ? [6.146195694628796, 46.193580525253395]
                  : [6.14693166748441, 46.19910831062394],
                zoom: isMobile ? 13 : 14.25,
                annotations: [{ type: 'symbol', id: 'paykin' }]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    In der Schweiz unerwünscht ist auch der russische
                    Duma-Abgeordnete <span className='red'>Boris Paikin.</span>{' '}
                    Der 56-Jährige wohnte zwischenzeitlich im Zentrum Genfs und
                    beteiligte sich unter anderem an einem Sägewerk. Ein
                    früherer FDP-Politiker machte bis 2014 Geschäfte mit dem
                    russischen Unternehmer. Er erinnert sich an einen «sehr
                    verschwiegenen Menschen», der die Schweiz nach einem
                    Missmanagement verlassen habe.
                  </p>
                  <Image
                    file={'paykin.jpg'}
                    source={'Screenshot'}
                    caption={
                      'Der Duma-Abgeordnete Boris Paikin steht auf der Sanktionsliste. Bis 2014 war er geschäftlich in der Schweiz tätig.'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },

            {
              o: {
                id: 'Lausanne',
                distance: 220,
                center: isMobile
                  ? [6.63029395322305, 46.506026438688295]
                  : [6.629832897231153, 46.526559194267776],
                zoom: isMobile ? 11.5 : 12,
                annotations: [{ type: 'symbol', id: 'usmanov' }],
                visibility: [{ id: 'city_labels', visible: true }]
              },
              e: (
                <MapRelatedBox>
                  <p>
                    In Lausanne engagiert sich der{' '}
                    <span className='red'>Oligarch Alischer Usmanow</span> für
                    den Fechtsport und sitzt in der Fondation pour le Musée
                    International de l'Escrime. Auch dieser schwerreiche Russe
                    (geschätztes Vermögen: 16,6 Milliarden Dollar) steht auf der
                    Sanktionsliste. Laut dem russischen Oppositionellen Alexei
                    Nawalny hat Usmanow einen Wohnsitz in Lausanne. Davon
                    distanziert sich die Waadtländer Regierung. Der Russe
                    besitze lediglich ein Schengen-Visum, mit dem er jährlich
                    drei Monate in der Schweiz weilen dürfe.
                  </p>
                  <Image
                    file={'usmanov.jpg'}
                    source={'Mikhail Svetlov / Getty Images'}
                    caption={
                      'Alischer Usmanow und Wladimir Putin bei der Staatspreisverleihung 2018 im Kreml'
                    }
                    onDark
                  />
                </MapRelatedBox>
              )
            },

            {
              o: {
                id: 'TEXT_END',
                distance: 180,
                ...viewSwitzerland,
                visibility: [{ id: 'city_labels', visible: false }]
              },
              e: (
                <TextBox>
                  <p>
                    Dass es zu Sanktionsumgehungen der Russen kommen wird, steht
                    für Strafrechtler Pieth ausser Frage. Er vermutet, dass
                    bereits seit der Krim-Annexion 2014 durch Russland
                    Strukturen geschaffen wurden, um Gelder in der Schweiz
                    besser zu verstecken.
                  </p>

                  <p>
                    Russische Geschäfte dürften in Zukunft in der Schweiz
                    unsichtbarer und damit Einträge im Handelsregister seltener
                    vorgenommen werden, glaubt Pieth. «Zumindest bislang mussten
                    sich die Russen in der Schweiz nicht verstecken», sagt er.
                  </p>
                  <Image
                    file={'pieth.jpg'}
                    source={'Walter Bieri/Keystone'}
                    caption={
                      '«Zumindest bislang mussten sich die Russen in der Schweiz nicht verstecken», sagt Geldwäscherei-Experte Mark Pieth.'
                    }
                  />
                </TextBox>
              )
            }
          ]}
          back={(index, options) => {
            if (options) {
              const {
                id,
                center,
                zoom = 7,
                pitch = 0,
                annotations,
                changeStyle,
                visibility
              } = options.o
              if (map.current) {
                let m = map.current
                setLayerSource(m, 'annotations_line', 'empty')
                setLayerSource(m, 'annotations_shape', 'empty')
                setLayerSource(m, 'annotations_text', 'empty')
                setLayerSource(m, 'annotations_circle', 'empty')
                setLayerSource(m, 'annotations_symbol', 'empty')
                toggleVisibility(m, 'annotations_line', false)
                toggleVisibility(m, 'annotations_shape', false)
                toggleVisibility(m, 'annotations_text', false)
                toggleVisibility(m, 'annotations_symbol', false)
                toggleVisibility(m, 'annotations_circle', false)

                if (id) {
                  changeScrollId(id)
                  if (id === 'TEXT_END') {
                    setShwoScroll(false)
                  } else {
                    setShwoScroll(true)
                  }
                }

                if (center) {
                  m.flyTo({
                    curve: Math.pow(6, 0.25),
                    center: center,
                    zoom: zoom,
                    essential: true,
                    pitch: pitch
                  })
                }

                if (annotations) {
                  annotations.map(
                    (annotation: { id: string; type: string }) => {
                      const { type, id } = annotation

                      switch (type) {
                        case 'line':
                          toggleVisibility(m, 'annotations_line', true)
                          setLayerSource(m, 'annotations_line', id)
                          break
                        case 'text':
                          toggleVisibility(m, 'annotations_text', true)
                          setLayerSource(m, 'annotations_text', id)
                          break
                        case 'shape':
                          toggleVisibility(m, 'annotations_shape', true)
                          setLayerSource(m, 'annotations_shape', id)
                          break
                        case 'circle':
                          toggleVisibility(m, 'annotations_circle', true)
                          setLayerSource(m, 'annotations_circle', id)
                          break
                        case 'symbol':
                          toggleVisibility(m, 'annotations_symbol', true)
                          setLayerSource(m, 'annotations_symbol', id)

                          break
                      }
                    }
                  )
                }

                if (changeStyle) {
                  changeStyle.map(
                    ({
                      id,
                      prop,
                      value
                    }: {
                      id: string
                      prop: string
                      value: string | number
                    }) => {
                      m.setPaintProperty(id, prop, value)
                    }
                  )
                }
                if (visibility) {
                  visibility.map(
                    ({ id, visible }: { id: string; visible: boolean }) => {
                      m.setLayoutProperty(
                        id,
                        'visibility',
                        visible ? 'visible' : 'none'
                      )
                    }
                  )
                }
              }
            }
            return (
              <>
                <Map
                  showScroll={showScroll}
                  getMap={m => {
                    m.addSource('empty', {
                      type: 'geojson',
                      data: {
                        type: 'Feature',
                        geometry: {
                          type: 'Point',
                          coordinates: []
                        },
                        properties: {}
                      }
                    })

                    m.loadImage(textbg, (error, image) => {
                      if (error) throw error
                      m.addImage('textbg', image)
                    })
                    m.loadImage(markerarrow, (error, image) => {
                      if (error) throw error
                      m.addImage('markerarrow', image)
                    })
                    m.loadImage(textbg_main, (error, image) => {
                      if (error) throw error
                      m.addImage('textbg_main', image)
                    })
                    m.loadImage(textbg_white, (error, image) => {
                      if (error) throw error
                      m.addImage('textbg_white', image)
                    })

                    geojsonsources.map(source => {
                      m.addSource(source.id, {
                        type: 'geojson',
                        data: source.json
                      })
                    })
                    m.addLayer({
                      id: 'spots',
                      type: 'circle',
                      source: 'spots',
                      paint: spotLayerStyle.paint,
                      layout: spotLayerStyle.layout
                    })
                    m.addLayer({
                      id: 'industry',
                      type: 'circle',
                      source: 'industry',
                      paint: industryLayerStyle.paint,
                      layout: industryLayerStyle.layout
                    })

                    m.addLayer({
                      type: 'line',
                      source: 'empty',
                      id: 'annotations_line',
                      paint: lineLayerStyle.paint,
                      layout: lineLayerStyle.layout
                    })

                    m.addLayer({
                      type: 'fill',
                      source: 'empty',
                      id: 'annotations_shape',
                      paint: shapeLayerStyle.paint
                    })
                    m.addLayer({
                      type: 'symbol',
                      source: 'empty',
                      id: 'annotations_symbol',
                      paint: symbolAbnnotationStyle.paint,
                      layout: symbolAbnnotationStyle.layout
                    })

                    m.addLayer({
                      type: 'symbol',
                      source: 'empty',
                      id: 'annotations_text',
                      paint: symbolLayerStyle.paint,
                      layout: symbolLayerStyle.layout
                    })

                    m.addLayer({
                      type: 'symbol',
                      source: 'citylabels',
                      id: 'city_labels',
                      paint: cityLabelsStyle.paint,
                      layout: cityLabelsStyle.layout
                    })
                    m.addLayer({
                      type: 'circle',
                      source: 'empty',
                      id: 'annotations_circle',
                      paint: circleLayerStyle.paint,
                      layout: circleLayerStyle.layout
                    })

                    map.current = m
                  }}
                />
              </>
            )
          }}
        />
      </div>
      <div className='ui vertical segment'></div>
      <Footer
        articleId={ARTICLE_ID}
        credits={[
          [
            'Methodik',
            'Für die vorliegende Analyse hat das Tamedia-Datenteam 5,5 Millionen Einträge der Schweizer Handelsregister ausgewertet. Die Daten decken den Zeitraum von 2005 bis 2022 ab und wurden von der Crif AG zur Verfügung gestellt. Die genannten Branchen (Grosshandel, IT, Finanzen und Versicherungen sowie Management-Dienstleistungen) basieren auf der offiziellen Schweizer Branchencodierung Noga. Auf der Karte wird jeweils ein Punkt pro Firma dargestellt. Die Punkte entsprechen den ungefähren Standorten und wurden mittels Google-Maps-Schnittstelle aus den Adressen ermittelt.'
          ],
          ['Text', 'Martin Stoll, Dominik Balmer'],
          ['Datenanalyse', 'Patrick Meier'],
          ['Gestaltung und Umsetzung', 'Sebastian Broschinski']
        ]}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gut funktionierte die technische Umsetzung?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
      />
    </div>
  )
}
